/**
 * Enhanced Google Analytics 4 tracking helpers for Wisconsin Parcel Explorer.
 * Builds on top of the basic analytics.ts wrapper with session management,
 * deduplication, and source tracking across user journeys.
 */

import { trackEvent } from './analytics';
import { tagSession } from './analytics-identity';
import { logUsage, type UsageMeta } from './usage-events';
import { noteArrival } from './acquisition';

// Session storage keys
const SESSION_KEYS = {
  SOURCE: 'analytics_source',
  SOURCE_SLUG: 'analytics_source_slug',
  SOURCE_COUNTY: 'analytics_source_county',
  FIRST_INTERACTION_TRACKED: 'analytics_first_interaction_tracked',
  ENTRY_PAGE: 'analytics_entry_page',
  LAST_LOCKED_FEATURE: 'analytics_last_locked_feature',
  LAST_PAYWALL: 'analytics_last_paywall',
  CURRENT_PATH: 'analytics_current_path',
  PREVIOUS_PATH: 'analytics_previous_path',
} as const;

/**
 * Analytics source type
 */
export type AnalyticsSource = 'blog' | 'county' | EntrySource;

/**
 * What a session that never touched a blog post or county page is credited
 * to: the page it started on. These used to be one undifferentiated 'direct',
 * which was about a third of all sign-ups — mostly people who started on the
 * home page or statewide search, the page that produces the most sign-ups.
 */
type EntrySource = 'home' | 'statewide' | 'pricing' | 'market' | 'direct';

const ENTRY_SOURCES: Record<string, EntrySource> = {
  '/': 'home',
  '/map': 'statewide',
  '/pricing': 'pricing',
  '/market': 'market',
};

/** The session source, falling back to the page the session started on. */
function currentSource(): string {
  return getSessionStorage(SESSION_KEYS.SOURCE) || ENTRY_SOURCES[getSessionStorage(SESSION_KEYS.ENTRY_PAGE) ?? ''] || 'direct';
}

/**
 * Initialize analytics session.
 * Should be called once on app load.
 * Note: UTM parameters (utm_source, utm_medium, utm_campaign) are captured
 * automatically by GA4 — no manual handling needed.
 *
 * @example
 * useEffect(() => {
 *   initializeAnalyticsSession()
 * }, [])
 */
export function initializeAnalyticsSession() {
  // GA4 automatically captures UTM params and traffic source attribution.
  //
  // `app_load` is the usage log's heartbeat: without it an account that only
  // browses — never exports, never filters — would look identical to one that
  // never came back, and "is this subscriber dormant" is the question it is
  // there to answer. Path only, never the query string: `?owner=` carries
  // whatever name someone typed. Referrer host only, for the same reason.
  let referrerHost: string | null = null;
  try {
    referrerHost = document.referrer ? new URL(document.referrer).host : null;
  } catch {
    referrerHost = null;
  }
  logUsage('app_load', {
    meta: { path: window.location.pathname, referrer_host: referrerHost },
  });

  // The first page of the session, kept for the rest of it. Runs on every full
  // page load (a reload, the return from Stripe or Google), so only the first
  // one writes. Path only: the query string can carry a typed owner name.
  if (!getSessionStorage(SESSION_KEYS.ENTRY_PAGE)) {
    setSessionStorage(SESSION_KEYS.ENTRY_PAGE, normalizeEntryPath(window.location.pathname));
  }

  // Where this visitor first came from, across visits (acquisition.ts): the
  // channel a later sign-up or purchase is credited to.
  noteArrival();
}

function normalizeEntryPath(pathname: string): string {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return (trimmed || '/').toLowerCase().slice(0, 100);
}

/**
 * Track a blog post view and set the blog as the session source.
 * Stores slug and category in session storage for downstream conversion tracking.
 *
 * @param slug - Blog post slug/identifier
 * @param title - Blog post title
 * @param category - Blog post category (e.g., 'guide', 'market-analysis')
 *
 * @example
 * trackBlogPostView('solar-farms-2024', 'Top Solar Farms in WI', 'guide')
 */
export function trackBlogPostView(slug: string, title: string, category: string) {
  // Set blog as the source in session storage
  setSessionStorage(SESSION_KEYS.SOURCE, 'blog');
  setSessionStorage(SESSION_KEYS.SOURCE_SLUG, slug);

  trackEvent('blog_post_view', {
    blog_slug: slug,
    blog_title: title,
    blog_category: category,
  });
}

/**
 * Track a click on a CTA (call-to-action) within a blog post.
 * Should be called when user interacts with links/buttons that navigate to the map tool.
 *
 * @param slug - Blog post slug where CTA was clicked
 * @param title - Blog post title
 * @param category - Blog post category
 *
 * @example
 * trackBlogCTAClick('solar-farms-2024', 'Top Solar Farms in WI', 'guide')
 */
export function trackBlogCTAClick(slug: string, title: string, category: string) {
  trackEvent('blog_cta_click', {
    blog_slug: slug,
    blog_title: title,
    blog_category: category,
    internal_source: getSessionStorage(SESSION_KEYS.SOURCE) || 'blog',
  });
}

/**
 * Track a county page view and set the county as the session source.
 * Stores county info in session storage for downstream conversion tracking.
 *
 * @param countyName - Full county name (e.g., 'Dane County')
 * @param countyFips - Optional FIPS code for the county
 *
 * @example
 * trackCountyPageView('Dane County', '55025')
 */
export function trackCountyPageView(countyName: string, countyFips?: string) {
  // Only set county as source if no source is already set (don't overwrite blog/existing lead source)
  const existingSource = getSessionStorage(SESSION_KEYS.SOURCE);
  if (!existingSource || existingSource === 'direct') {
    setSessionStorage(SESSION_KEYS.SOURCE, 'county');
  }
  setSessionStorage(SESSION_KEYS.SOURCE_COUNTY, countyName);

  trackEvent('county_page_view', {
    county_name: countyName,
    county_fips: countyFips,
  });
}

/**
 * Track map load event with context about the source that brought user to the map.
 * Uses session storage to include source information (blog, county, or direct).
 *
 * @example
 * trackMapLoaded() // Will include source context from session storage
 */
export function trackMapLoaded() {
  const internalSource = currentSource();
  const sourceSlug = getSessionStorage(SESSION_KEYS.SOURCE_SLUG);
  const sourceCounty = getSessionStorage(SESSION_KEYS.SOURCE_COUNTY);

  const params: Record<string, string | number | boolean | null | undefined> = {
    internal_source: internalSource,
  };

  if (sourceSlug) {
    params.source_slug = sourceSlug;
  }

  if (sourceCounty) {
    params.source_county = sourceCounty;
  }

  trackEvent('map_loaded', params);
}

/**
 * Track map interactions (pan, zoom, filter, search, etc.).
 * Fires a `first_map_interaction` event once per session for funnel analysis,
 * then tracks all subsequent interactions as `map_interaction`.
 *
 * @param interactionType - Type of interaction (e.g., 'pan', 'zoom', 'filter_applied', 'search')
 *
 * @example
 * trackMapInteraction('zoom')
 * trackMapInteraction('filter_applied')
 */
export function trackMapInteraction(interactionType: string) {
  const internalSource = currentSource();
  const isFirst = !getSessionStorage(SESSION_KEYS.FIRST_INTERACTION_TRACKED);

  if (isFirst) {
    setSessionStorage(SESSION_KEYS.FIRST_INTERACTION_TRACKED, 'true');
    trackEvent('first_map_interaction', {
      interaction_type: interactionType,
      internal_source: internalSource,
    });
  }

  trackEvent('map_interaction', {
    interaction_type: interactionType,
    internal_source: internalSource,
  });
}

/**
 * Track map conversions (exports, reports, saved searches, etc.).
 * Includes source context from session storage.
 *
 * @param conversionType - Type of conversion (e.g., 'export_csv', 'export_shapefile', 'report_generated')
 * @param params - Optional additional parameters to include in the event
 *
 * @example
 * trackMapConversion('export_csv', { format: 'csv', row_count: 1234 })
 * trackMapConversion('report_generated')
 */
export function trackMapConversion(
  conversionType: ConversionType,
  params?: Record<string, string | number | boolean | null | undefined>
) {
  trackEvent('map_conversion', withSourceContext({ conversion_type: conversionType, ...params }));

  // The same moment, attributable. This is the row that answers "did they
  // export one CSV and cancel": GA4 records that *an* export happened, the
  // usage log records whose. Routing it through here means a conversion added
  // later is logged without anyone remembering to.
  const { county, ...rest } = params ?? {};
  const meta: UsageMeta = {};
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined) meta[key] = value;
  }
  logUsage(conversionType, { county: typeof county === 'string' ? county : null, meta });
}

/**
 * The conversions the Lite plan sells. Each is also a usage-log event name, so
 * a new one has to be added to `UsageEventName` too — which is the point.
 */
export type ConversionType = 'export_csv' | 'parcel_map_download' | 'parcel_gpx_download';

type EventParams = Record<string, string | number | boolean | null | undefined>;

/**
 * Stamp an event with where this session came from.
 *
 * `trackBlogPostView` / `trackCountyPageView` record the source; this is what
 * reads it back. Every funnel event carries it, which is the whole point:
 * "300 signups" is not useful, "300 signups, 60% of them off a blog post, and
 * here's which post" is.
 */
function withSourceContext(params: EventParams = {}): EventParams {
  const sourceSlug = getSessionStorage(SESSION_KEYS.SOURCE_SLUG);
  const sourceCounty = getSessionStorage(SESSION_KEYS.SOURCE_COUNTY);
  const entryPage = getSessionStorage(SESSION_KEYS.ENTRY_PAGE);

  return {
    internal_source: currentSource(),
    ...(sourceSlug ? { source_slug: sourceSlug } : {}),
    ...(sourceCounty ? { source_county: sourceCounty } : {}),
    ...(entryPage ? { entry_page: entryPage } : {}),
    ...params,
  };
}

/**
 * Paywall sources that are a general Upgrade button, not a locked feature.
 * Opening one says nothing about what the person wanted, so it never replaces
 * the feature an earlier paywall recorded: someone stopped at the CSV export
 * who then clicks Upgrade in the sidebar bought for the CSV export. Until
 * 2026-10 it did replace it, and a third of purchases were credited to
 * `sidebar_upgrade` or `pricing_page`. A new general button goes here.
 */
export const UPGRADE_BUTTON_SOURCES: ReadonlySet<string> = new Set([
  'sidebar_upgrade',
  'account_menu',
  'account_settings',
  'pricing_page',
  'unknown',
]);

/**
 * The last locked feature this browser hit, kept across tabs and visits for
 * `RECENT_FEATURE_TTL_MS`. Session storage is one tab's: a buyer stopped by
 * the statewide daily limit at 18:00 who came back in a new tab at 18:52 and
 * bought from Account settings was credited to `account_settings`
 * (2026-10-27). Not an identifier, only a paywall's name and when.
 */
export const RECENT_FEATURE_KEY = 'wpm_last_locked_feature';
export const RECENT_FEATURE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function rememberRecentFeature(feature: string, now = Date.now()): void {
  if (typeof window !== 'undefined' && window.__storageConsent === false) return;
  try {
    localStorage.setItem(RECENT_FEATURE_KEY, JSON.stringify({ feature, at: now }));
  } catch {
    // Storage blocked: this tab's session copy still answers.
  }
}

function recentFeature(now = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(RECENT_FEATURE_KEY);
    if (!raw) return null;
    const { feature, at } = JSON.parse(raw) as { feature?: unknown; at?: unknown };
    if (typeof feature !== 'string' || !/^[a-z0-9_]{1,64}$/.test(feature)) return null;
    if (typeof at !== 'number' || now - at > RECENT_FEATURE_TTL_MS || at > now) return null;
    return feature;
  } catch {
    return null;
  }
}

/**
 * The feature that sold Lite, for `begin_checkout` and `purchase`: the last
 * locked feature this tab hit, else the last one this browser hit in the
 * past week (another tab, an earlier visit), else the Upgrade button that
 * opened the paywall when no feature was hit. `paywall_hit` says which gate
 * people meet; this says which one they paid at, without joining events by
 * hand one buyer at a time. A purchase on another device is credited from
 * the account's own history instead (`v_purchase_attribution`).
 */
export function getLastLockedFeature(now = Date.now()): string | null {
  return (
    getSessionStorage(SESSION_KEYS.LAST_LOCKED_FEATURE) ??
    recentFeature(now) ??
    getSessionStorage(SESSION_KEYS.LAST_PAYWALL)
  );
}

/**
 * Where checkout was clicked: `pricing_page` on /pricing, else the paywall
 * or Upgrade button last opened. With `getLastLockedFeature` it separates the
 * feature that made the sale from the button that closed it.
 */
export function getUpgradeEntry(pathname: string): string | null {
  if (normalizeEntryPath(pathname) === '/pricing') return 'pricing_page';
  return getSessionStorage(SESSION_KEYS.LAST_PAYWALL);
}

function withLockedFeature(params: EventParams): EventParams {
  const lockedFeature = getLastLockedFeature();
  return lockedFeature ? { locked_feature: lockedFeature, ...params } : params;
}

/** How the account was created or entered. */
export type AuthMethod = 'password' | 'google';

/**
 * GA4's standard `sign_up`. Free-plan account creation.
 *
 * Carries the session source, so the report answers "what were they doing
 * when they decided to sign up" rather than just counting accounts.
 */
export function trackSignUp(method: AuthMethod) {
  trackEvent('sign_up', withSourceContext({ method }));
}

/** GA4's standard `login`. Separate from sign_up so neither inflates the other. */
export function trackLogin(method: AuthMethod) {
  trackEvent('login', withSourceContext({ method }));
}

/**
 * A locked feature was hit — the modal opened.
 *
 * `locked_feature` is the question this exists to answer: which gate actually
 * drives upgrades. Fired from the modals themselves rather than from each of
 * the dozen call sites, so a new gate can't forget to report.
 */
export function trackPaywallHit(lockedFeature: string, plan: 'lite' | 'pro' = 'lite', extra: EventParams = {}) {
  // Tag the Clarity session too. The GA4 event answers "how many people hit
  // the CSV gate"; the tag makes those specific replays findable, which is the
  // only way to see what they did *next* — closed the modal, went back to the
  // table, left the site. Clarity accumulates repeated values under one key,
  // so someone who hits two gates carries both.
  tagSession('hit_paywall', lockedFeature);
  setSessionStorage(SESSION_KEYS.LAST_PAYWALL, lockedFeature);
  if (!UPGRADE_BUTTON_SOURCES.has(lockedFeature)) {
    setSessionStorage(SESSION_KEYS.LAST_LOCKED_FEATURE, lockedFeature);
    rememberRecentFeature(lockedFeature);
  }

  trackEvent('paywall_hit', withSourceContext({ locked_feature: lockedFeature, plan, ...extra }));

  // Signed-in hits also go to the first-party log (a no-op when signed out),
  // so a paying customer's path to purchase survives an ad blocker. The
  // signed-out hits — most of them — are GA4's, joined to the account that
  // follows through BigQuery's user_pseudo_id.
  logUsage('paywall_hit', { meta: { locked_feature: lockedFeature, plan_offered: plan } });
}

/**
 * A paywall was closed without starting checkout. With `paywall_hit` and
 * `begin_checkout` it gives every gate its three outcomes: closed, clicked
 * through, or left the page with it open. `seconds_open` separates a glance
 * from a read; capped so a modal left open in a background tab does not
 * skew an average.
 */
export function trackPaywallClose(lockedFeature: string, plan: 'lite' | 'pro', openedAt: number, extra: EventParams = {}) {
  const seconds = Math.min(600, Math.round((Date.now() - openedAt) / 1000));
  trackEvent('paywall_close', withSourceContext({ locked_feature: lockedFeature, plan, seconds_open: seconds, ...extra }));
}

/**
 * Someone took what their plan offers instead, from inside a paywall (the GPX
 * gate's free map image). Sent with the `paywall_close` it also counts as, so
 * a close that kept the person working is not read as one that lost them.
 */
export function trackPaywallAlternative(lockedFeature: string) {
  trackEvent('paywall_alternative', withSourceContext({ locked_feature: lockedFeature }));
}

/**
 * Someone asked to hear about Pro. The teaser has no checkout, so this is the
 * only signal of demand for what it describes, gate by gate.
 */
export function trackProWaitlistClick(lockedFeature: string) {
  trackEvent('pro_waitlist_click', withSourceContext({ locked_feature: lockedFeature }));
  logUsage('pro_waitlist_click', { meta: { locked_feature: lockedFeature } });
}

/**
 * The pricing page opened. `pricing_from` is the in-app page that led there, so a
 * purchase made on /pricing can be traced past "pricing_page" to whatever
 * sent them: a paywall, the footer, a blog post. `locked_feature` is the last
 * gate this session hit, if any.
 */
export function trackPricingView() {
  // Read whichever way the effects ran: the route listener may or may not
  // have recorded /pricing itself yet.
  const current = getSessionStorage(SESSION_KEYS.CURRENT_PATH);
  const from = current === '/pricing' ? getSessionStorage(SESSION_KEYS.PREVIOUS_PATH) : current;
  trackEvent('pricing_view', withSourceContext(withLockedFeature({ pricing_from: from ?? 'external' })));
}

/**
 * Called on every route change (`RouteHistory`). Keeps the last two paths —
 * path only, never the query string, which can carry a typed owner name.
 */
export function notePathChange(pathname: string) {
  const path = normalizeEntryPath(pathname);
  const current = getSessionStorage(SESSION_KEYS.CURRENT_PATH);
  if (current === path) return;
  if (current) setSessionStorage(SESSION_KEYS.PREVIOUS_PATH, current);
  setSessionStorage(SESSION_KEYS.CURRENT_PATH, path);
}

/**
 * A guest buyer getting back in after paying: resending the emailed link
 * (`guest_checkout_link_resend`) or Google (`guest_checkout_google`).
 * Source-stamped like the rest of the funnel, so the last step of a guest
 * purchase still says which post or county sold it.
 */
export function trackGuestCheckoutSignIn(via: 'link_resend' | 'google', params: EventParams = {}) {
  trackEvent(`guest_checkout_${via}`, withSourceContext(params));
}

/** GA4's standard `begin_checkout`, fired as we hand off to Stripe. */
export function trackBeginCheckout(params: EventParams = {}) {
  trackEvent('begin_checkout', withSourceContext(withLockedFeature({ currency: 'USD', ...params })));
}

/**
 * GA4's standard `purchase`, fired once entitlement actually flips.
 *
 * `value` and `currency` come from the Stripe session, never from a constant
 * in this repo — the account's previous yearly price was $50, not $10, and a
 * hardcoded number would have silently reported the wrong revenue for months.
 * Omitted entirely when Stripe didn't give us an amount, because a confidently
 * wrong number is worse than a missing one.
 */
export function trackPurchase(params: {
  transactionId?: string;
  value?: number;
  currency?: string;
  /** `getUpgradeEntry` as it was when checkout started, carried through Stripe. */
  upgradeEntry?: string | null;
}) {
  trackEvent(
    'purchase',
    withSourceContext(
      withLockedFeature({
        transaction_id: params.transactionId,
        ...(params.upgradeEntry ? { upgrade_entry: params.upgradeEntry } : {}),
        currency: params.currency || 'USD',
        ...(typeof params.value === 'number' && Number.isFinite(params.value)
          ? { value: params.value }
          : {}),
      }),
    ),
  );
}

/** Which tab of /auth someone is on, or the Google button that skips both. */
export type AuthMode = 'signin' | 'signup';

/**
 * Why an attempt on /auth did not end in a session. Categories only, never
 * the message: Supabase's error text can echo the address that was typed.
 */
export type AuthErrorReason =
  | 'invalid_credentials'
  | 'email_not_confirmed'
  | 'already_registered'
  | 'resend_throttled'
  | 'invalid_input'
  | 'oauth_failed'
  | 'other';

/**
 * The sign-up page opened. With `auth_attempt` and `auth_error` this is the
 * funnel `sign_up` alone can't show: people who opened it and left, which
 * method they chose, and how often it failed. `redirect_path` is where they
 * were trying to go (path only; a query string can carry a typed name).
 */
export function trackAuthView(redirectTo: string, tab: AuthMode = 'signin') {
  let redirectPath = '/';
  try {
    redirectPath = new URL(redirectTo, window.location.origin).pathname;
  } catch {
    redirectPath = '/';
  }
  trackEvent('auth_view', withSourceContext({ redirect_path: redirectPath, tab }));
}

export function trackAuthAttempt(method: AuthMethod, mode: AuthMode) {
  trackEvent('auth_attempt', withSourceContext({ method, mode }));
}

/**
 * After a failed sign-in, the person chose "create a free account" instead.
 * Supabase cannot say whether the account exists, so this is the only sign of
 * how many "invalid credentials" were a visitor with no account yet.
 */
export function trackAuthSwitchToSignUp(after: 'invalid_credentials') {
  trackEvent('auth_switch_to_signup', withSourceContext({ after }));
}

export function trackAuthError(method: AuthMethod, mode: AuthMode, reason: AuthErrorReason) {
  trackEvent('auth_error', withSourceContext({ method, mode, reason }));
}

/**
 * An email sign-up is waiting on its confirmation link. Half of them once
 * never came back; `sign_up` firing later (from the link) closes the loop.
 */
export function trackAuthAwaitingConfirmation(mode: AuthMode) {
  trackEvent('auth_awaiting_confirmation', withSourceContext({ method: 'password', mode }));
}

/**
 * The free-account prompt on a county parcel card (`SaveParcelPrompt`):
 * `view` once per session, then `click` or `dismiss`. A `sign_up` after a
 * `click` from the same browser is the prompt's conversion.
 */
export function trackSavePrompt(action: 'view' | 'click' | 'dismiss', surface: 'card' | 'sheet') {
  trackEvent('save_prompt', withSourceContext({ action, surface }));
}

/**
 * A new link asked for after an expired one: `via` is the toast's one tap
 * (address remembered on this device) or the `/auth?link=expired` form.
 * Never the address.
 */
export function trackAuthLinkResend(via: 'toast' | 'form', outcome: 'sent' | 'throttled' | 'no_account' | 'error') {
  trackEvent('auth_link_resend', { via, outcome });
}

/**
 * Internal helper: Get a value from session storage
 */
function getSessionStorage(key: string): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(key);
}

/**
 * Internal helper: Set a value in session storage
 */
function setSessionStorage(key: string, value: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(key, value);
}
